import React from "react";
import { Tab } from "@commonsku/styles";
import { useDispatch } from "react-redux";
import { CatalogItem, CatalogItemsSortField } from "../../../types";
import type { CatalogItemsFilters } from "./CatalogPage";
import { CatalogPublishedView } from "./CatalogPublishedView";
import { CatalogUnpublishedView } from "./CatalogUnpublishedView";
import { useIdentity } from "../../../hooks";
import { getIdentityUtils } from "../../../utils";
import { deleteCatalogItem } from "../../../redux/catalog";
import { createConfirmationForDeletePopup } from "../../../actions/popup";
import {
    TAB_UNPUBLISHED,
    TAB_PUBLISHED,
    ItemsTabBar,
    type CatalogItemsTab,
} from "./catalogShared";

interface CatalogItemsPageProps {
    activeTab: CatalogItemsTab;
    publishedCount: number;
    unpublishedCount: number;
    publishedItems: CatalogItem[];
    unpublishedItems: CatalogItem[];
    onTabChange: (tab: CatalogItemsTab) => void;
    filters: CatalogItemsFilters;
    onFiltersChange: (patch: Partial<CatalogItemsFilters>) => void;
    onApplyFilters: () => void;
    onSortField: (field: CatalogItemsSortField) => void;
    onSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    catalogId: string | null;
    hasMore: boolean;
    loadingMore: boolean;
    onLoadMore: () => void;
    onAddProduct: () => void;
    onEditItem: (itemId: string) => void;
    hasEverHadPublishedItems: boolean;
}

export const CatalogItemsPage = ({
    activeTab,
    publishedCount,
    unpublishedCount,
    publishedItems,
    unpublishedItems,
    onTabChange,
    filters,
    onFiltersChange,
    onApplyFilters,
    onSortField,
    onSearchKeyDown,
    catalogId,
    hasMore,
    loadingMore,
    onLoadMore,
    onAddProduct,
    onEditItem,
    hasEverHadPublishedItems,
}: CatalogItemsPageProps) => {
    const listViewProps = {
        filters,
        onFiltersChange,
        onApplyFilters,
        onSortField,
        onSearchKeyDown,
        hasMore,
        loadingMore,
        onLoadMore,
    };

    const dispatch = useDispatch();
    const identity = useIdentity();
    const { hasCapabilities, hasUserFlags } = getIdentityUtils(identity);
    const canDeleteItems =
        hasCapabilities(["DELETE-INACTIVE-CATALOG-ITEM"]) &&
        hasUserFlags(["CLIENT-CATALOG"]);
    const deleteItem = (itemId: string) => {
        if (!canDeleteItems || !itemId || !catalogId) return;
        dispatch(deleteCatalogItem({ catalogId, itemId }) as any);
    };
    const handleDeleteItem = (item: CatalogItem) => {
        if (!canDeleteItems) return;
        dispatch(
            createConfirmationForDeletePopup(
                deleteItem,
                [item.item_id],
                "the",
                item.item_name,
                false,
                "SKUs associated to this product will no longer work, please check external systems for references.",
            ),
        );
    };

    return (
        <>
            <ItemsTabBar>
                <Tab
                    variant="secondary"
                    selected={activeTab === TAB_UNPUBLISHED}
                    onClick={() => { onTabChange(TAB_UNPUBLISHED) }}
                >
                    Unpublished ({unpublishedCount})
                </Tab>
                <Tab
                    variant="secondary"
                    selected={activeTab === TAB_PUBLISHED}
                    onClick={() => { onTabChange(TAB_PUBLISHED) }}
                >
                    Published ({publishedCount})
                </Tab>
            </ItemsTabBar>
            {activeTab === TAB_UNPUBLISHED ? (
                <CatalogUnpublishedView
                    items={unpublishedItems}
                    {...listViewProps}
                    canDeleteItems={canDeleteItems}
                    onDeleteItem={handleDeleteItem}
                    onAddProduct={onAddProduct}
                    onEditItem={onEditItem}
                />
            ) : (
                <CatalogPublishedView items={publishedItems} hasEverHadPublishedItems={hasEverHadPublishedItems} {...listViewProps} />
            )}
        </>
    );
};
